import { GetStaticProps } from 'next';
import { TPropsAttractionsPage } from '@/entities/Attractions/domain/Attractions.domain';
import { getFeaturedSectionTitle } from '@/shared/FeaturedActivities/ui/featuredActivitiesPreview/utils';
import { fetchLandmarksByCityId } from '@/screens/LandmarkMainPage/service/fetchLandmarksByCityId';
import { getRecommends } from '@/shared/FeaturedActivities/service/ApiFeaturedActivities';
import {
    getCardImagesWithPlaceholders,
    getPlaceholders,
} from '@/common/service/api/Activity/Activity';
import { getActivityDateless } from '@/entities/Attractions/service/ApiAttractionsPage';
import {
    getDestination,
    CreateAttractionsPageTitle,
    createAttractionsPageMeta
} from '@/entities/Attractions/service/Creators';
import { getCategoriesFeatured } from '@/shared/TopAttractions/service/ApiTopAttractions';
import AttractionsPage from 'pages/attractions/[destination]';
import destinationsList from '..//../../../../scripts/cache/destinationsList.json';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';

export async function getStaticPaths() {
    const paths = destinationsList.map((value) => ({
        params: {
            destination: value.slug.toLowerCase(),
            state: value.state.code.toLowerCase(),
        },
    }));

    return {
        paths,
        fallback: 'blocking',
    };
}

type PageStaticProps = {
    destination: string;
    state: string;
};

export const getStaticProps: GetStaticProps<TPropsAttractionsPage, PageStaticProps> = async ({
    params,
}) => {
    try {
        if (!params) {
            throw new Error(`PAGE THINGS TO DO /DESTINATION: BAD PARAMS: ${params}`);
        }

        const currentDestination = getDestination(params.destination || '');
        const currentPath = `/destination/${params.state}/${params.destination}/things-to-do/`;

        if (!currentDestination) {
            const redirectPath = await getRedirectsPath(currentPath);

            if (redirectPath === '/404/') {
                return {
                    notFound: true,
                };
            }

            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    statusCode: 301,
                },
            };
        }

        const rightPath =
            `/destination/${currentDestination.state.code}/${currentDestination.slug}/things-to-do/`.toLowerCase();

        if (currentPath !== rightPath) {
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: rightPath,
                    statusCode: 301,
                },
            };
        }

        const title = CreateAttractionsPageTitle(currentDestination);
        const featuredActivitiesTitle = getFeaturedSectionTitle(currentDestination.name);

        const breadcrumbs = [
            {
                url: '/destination/',
                title: 'Destination',
            },
            {
                url: `/destination/${currentDestination.state.code.toLowerCase()}/`,
                title: currentDestination.state.name,
            },
            {
                url: rightPath.replace('things-to-do/', ''),
                title: currentDestination.name,
            },
            {
                title: 'Things to do',
                url: rightPath,
                unlink: true,
            },
        ];

        const [fullList, featuredCategories, landmarks, recommends] = await Promise.all([
            getActivityDateless(currentDestination.id),
            getCategoriesFeatured(currentDestination.id),
            fetchLandmarksByCityId(currentDestination.id),
            getRecommends({
                destination_id: currentDestination.id,
            }),
        ]);

        const meta = createAttractionsPageMeta(currentDestination, undefined, fullList.total, {
            destinationDescription: fullList.destination_description,
        });

        const ids = fullList.items.map(({ id }) => id);

        const [images, placeholders] = await Promise.all([
            getCardImagesWithPlaceholders(ids),
            getPlaceholders(ids, 'activity'),
        ]);

        fullList.items.forEach((trip) => {
            //trip.price = 0;
            trip.images = images[trip.id] || [];
            trip.placeholder = placeholders[trip.id] || '';
        });

        if (featuredCategories.length) {
            featuredCategories.forEach((category) => {
                category.url = rightPath + category.slug + '/';
            });
        }

        return {
            revalidate: Number(process.env.CACHE_ENV),
            props: {
                meta,
                breadcrumbs,
                featuredCategories,
                title,
                featuredActivitiesTitle,
                fullList,
                currentDestination,
                isEmpty: !fullList.total,
                landmarks,
                recommendedTrips: recommends,
                availableAttributes: [],
                placeholders,
                recommendedIds: recommends.map(({ id }) => id),
            },
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default AttractionsPage;
